import { useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { Check, Circle, ImagePlus, Loader2, Plus, Sparkles, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, imgUrl, errMsg } from "@/lib/api";
import { FieldLabel, Panel } from "@/admin/ui";

export const ImagePanel = ({ form, patch }) => {
  const fileRef = useRef(null);
  const upload = useMutation({
    mutationFn: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/admin/upload", fd).then((r) => r.data); },
    onSuccess: (d) => { patch({ image_url: d.image_url, image_credit: "" }); toast.success("Imagen subida"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const ai = useMutation({
    mutationFn: () => api.post("/admin/ai/cover", { prompt: form.image_prompt || `${form.title}. ${form.summary}` }, { timeout: 180000 }).then((r) => r.data),
    onSuccess: (d) => { patch(d); toast.success("Imagen creada con IA"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const busy = upload.isPending || ai.isPending;
  return (
    <Panel title="Imagen de portada" testid="editor-image-panel">
      <div className="relative aspect-[16/10] bg-muted overflow-hidden">
        {form.image_url ? <img src={imgUrl(form.image_url)} alt="" className="h-full w-full object-cover" data-testid="editor-image-preview" /> : <div className="h-full w-full cover-fallback grid place-items-center text-sm text-muted-foreground">Sin imagen</div>}
        {busy && <div className="absolute inset-0 bg-background/70 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}
        {form.image_url && !busy && (
          <button onClick={() => patch({ image_url: null, image_credit: "" })} className="absolute top-2 right-2 h-7 w-7 grid place-items-center bg-background/90 hover:text-destructive" aria-label="Quitar imagen" data-testid="editor-image-remove"><X className="h-4 w-4" /></button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="editor-image-upload-btn"><ImagePlus /> Subir</Button>
        <Button variant="outline" onClick={() => ai.mutate()} disabled={busy || !form.title} data-testid="editor-image-ai-btn"><Sparkles /> Crear con IA</Button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload.mutate(e.target.files[0])} data-testid="editor-image-file-input" />
      <div className="mt-4">
        <FieldLabel>Texto alternativo (accesibilidad y SEO)</FieldLabel>
        <Input value={form.image_alt || ""} onChange={(e) => patch({ image_alt: e.target.value })} placeholder="Describe la imagen" data-testid="editor-image-alt-input" />
      </div>
    </Panel>
  );
};

const words = (html) => (html || "").replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const firstParagraph = (html) => ((html || "").match(/<p>(.*?)<\/p>/i)?.[1] || "").replace(/<[^>]+>/g, "");

const keywordChecks = (form, title, desc) => {
  const kw = norm(form.focus_keyword).trim();
  if (!kw) return [];
  const slugKw = kw.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return [
    ["Palabra clave en el título SEO", norm(title).includes(kw)],
    ["Palabra clave en la meta descripción", norm(desc).includes(kw)],
    ["Palabra clave en el primer párrafo", norm(firstParagraph(form.body)).includes(kw)],
    ["Palabra clave en la URL", (form.slug || "").includes(slugKw)],
  ];
};

export const SeoPanel = ({ form, patch }) => {
  const title = form.seo_title || form.title;
  const desc = form.seo_description || form.summary;
  const checks = [
    ["Título SEO entre 30 y 60 caracteres", title.length >= 30 && title.length <= 60],
    ["Meta descripción entre 70 y 155 caracteres", desc.length >= 70 && desc.length <= 155],
    ["Más de 300 palabras", words(form.body) >= 300],
    ["Al menos 3 claves (GEO)", form.key_points.filter(Boolean).length >= 3],
    ["Al menos 2 preguntas frecuentes (GEO)", form.faqs.filter((f) => f.q && f.a).length >= 2],
    ["Imagen con texto alternativo", !!form.image_url],
    ["Al menos 2 etiquetas", form.tags.length >= 2],
    ...keywordChecks(form, title, desc),
  ];
  const score = Math.round((checks.filter((c) => c[1]).length / checks.length) * 100);
  return (
    <Panel title="SEO y buscadores" description="Cómo aparecerá en Google y en los buscadores con IA." testid="editor-seo-panel">
      <div className="border border-border bg-background p-4" data-testid="editor-google-preview">
        <p className="text-xs text-emerald-700 dark:text-emerald-400 truncate">{window.location.host}/noticia/{form.slug || "…"}</p>
        <p className="text-[#1a0dab] dark:text-sky-400 text-[17px] leading-snug line-clamp-1 mt-0.5">{title || "Título de la noticia"}</p>
        <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{desc || "Descripción que verá el lector en los resultados."}</p>
      </div>
      <div className="mt-4 space-y-3">
        <div>
          <FieldLabel hint="opcional">Palabra clave principal</FieldLabel>
          <Input value={form.focus_keyword || ""} onChange={(e) => patch({ focus_keyword: e.target.value })} placeholder="Ej.: financiación pymes" data-testid="editor-focus-keyword-input" />
        </div>
        <div>
          <FieldLabel hint={`${(form.seo_title || "").length}/60`} warn={(form.seo_title || "").length > 60}>Título SEO</FieldLabel>
          <Input value={form.seo_title || ""} onChange={(e) => patch({ seo_title: e.target.value })} placeholder="Si lo dejas vacío se usa el titular" data-testid="editor-seo-title-input" />
        </div>
        <div>
          <FieldLabel hint={`${(form.seo_description || "").length}/155`} warn={(form.seo_description || "").length > 155}>Meta descripción</FieldLabel>
          <Textarea rows={3} value={form.seo_description || ""} onChange={(e) => patch({ seo_description: e.target.value })} placeholder="Si la dejas vacía se usa la entradilla" data-testid="editor-seo-description-input" />
        </div>
        <div>
          <FieldLabel>Dirección (URL)</FieldLabel>
          <Input value={form.slug || ""} onChange={(e) => patch({ slug: e.target.value })} placeholder="se-genera-sola-desde-el-titular" data-testid="editor-slug-input" />
        </div>
      </div>
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2"><span className="text-sm font-medium">Salud SEO / GEO</span><span className="font-mono text-sm text-primary" data-testid="editor-seo-score">{score}%</span></div>
        <div className="h-1.5 bg-muted"><div className="h-full bg-primary transition-all" style={{ width: `${score}%` }} /></div>
        <ul className="mt-3 space-y-1.5">
          {checks.map(([label, ok]) => (
            <li key={label} className={`flex items-center gap-2 text-sm ${ok ? "" : "text-muted-foreground"}`}>
              {ok ? <Check className="h-4 w-4 text-emerald-600" /> : <Circle className="h-3.5 w-3.5" />}{label}
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
};

export const KeyPointsPanel = ({ points, onChange }) => (
  <Panel title="Claves de la noticia" description="Frases cortas que resumen la noticia. Ayudan a Google y a las IA a citarte." testid="editor-keypoints-panel">
    <div className="space-y-2">
      {points.map((p, i) => (
        <div key={i} className="flex gap-2">
          <Input value={p} onChange={(e) => onChange(points.map((x, j) => (j === i ? e.target.value : x)))} placeholder={`Clave ${i + 1}`} data-testid={`editor-keypoint-${i}`} />
          <Button variant="ghost" size="icon" onClick={() => onChange(points.filter((_, j) => j !== i))} aria-label="Quitar clave" data-testid={`editor-keypoint-remove-${i}`}><Trash2 /></Button>
        </div>
      ))}
    </div>
    <Button variant="outline" size="sm" className="mt-3" onClick={() => onChange([...points, ""])} data-testid="editor-keypoint-add"><Plus /> Añadir clave</Button>
  </Panel>
);

export const FaqPanel = ({ faqs, onChange }) => (
  <Panel title="Preguntas frecuentes" description="Preguntas que la gente hace a los buscadores, con respuesta directa." testid="editor-faq-panel">
    <div className="space-y-4">
      {faqs.map((f, i) => (
        <div key={i} className="border border-border p-3 space-y-2">
          <div className="flex gap-2">
            <Input value={f.q} onChange={(e) => onChange(faqs.map((x, j) => (j === i ? { ...x, q: e.target.value } : x)))} placeholder="Pregunta" data-testid={`editor-faq-q-${i}`} />
            <Button variant="ghost" size="icon" onClick={() => onChange(faqs.filter((_, j) => j !== i))} aria-label="Quitar pregunta" data-testid={`editor-faq-remove-${i}`}><Trash2 /></Button>
          </div>
          <Textarea rows={2} value={f.a} onChange={(e) => onChange(faqs.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)))} placeholder="Respuesta" data-testid={`editor-faq-a-${i}`} />
        </div>
      ))}
    </div>
    <Button variant="outline" size="sm" className="mt-3" onClick={() => onChange([...faqs, { q: "", a: "" }])} data-testid="editor-faq-add"><Plus /> Añadir pregunta</Button>
  </Panel>
);
